import React, { useState } from 'react';
import Seo from './Seo';
import './HelpPage.css';

function HelpPage() {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');

  const helpSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: '🚀',
      content: [
        {
          question: 'How do I create a task?',
          answer: 'Type what you need in the composer bar at the bottom of the Tasks page and press Enter (or the send arrow). As you type, Tudu suggests matching tasks from your history and auto-detects a category (e.g. "buy eggs" → Shopping).'
        },
        {
          question: 'What do the "+" and options do on the task bar?',
          answer: 'Tap "+" to set a priority, pick or change the category, or upload a photo to scan items from it. When you start typing, a quick row also lets you flag Priority or set a Quantity for shopping items.'
        },
        {
          question: 'How do I move between days and weeks?',
          answer: 'Use the week tabs and day view on the Tasks page, or click the calendar button in the header to jump straight to any date.'
        },
        {
          question: 'How do I switch between light and dark mode?',
          answer: 'Open the Profile menu in the top-right and toggle Dark Mode, or use the same toggle on the Settings page. Your choice is remembered on this device.'
        }
      ]
    },
    {
      id: 'features',
      title: 'Features',
      icon: '✨',
      content: [
        {
          question: 'What can the "Ask" assistant do?',
          answer: 'Tap "Ask" in the header to open the Tudu assistant. It gives smart suggestions for your shopping cart, price tips and store comparisons, and can help you think through or adjust your planned tasks.'
        },
        {
          question: 'How do Daily routines work?',
          answer: 'On the Daily tab you can create recurring routines — pick the days and a time, or set an "every few hours" interval with a start/end window. Routines can be flagged as critical (like medication) with a dose note.'
        },
        {
          question: 'How do I share tasks with family or a team?',
          answer: 'On the Team tab, create a group, then open it and add tasks from the bar that appears. Use the "+" to assign a task to specific members, or leave it open to everyone in the group. Members can accept tasks to take them on.'
        },
        {
          question: 'What are Deals and the Wallet?',
          answer: 'The Deals tab shows live local deals and coupons you can save. Saved coupons go to your Wallet, where you can track active and used ones and keep a watchlist of deals you\'re interested in.'
        },
        {
          question: 'How does Plan (travel & discovery) work?',
          answer: 'On the Plan tab, create a trip or a "discover" task and Tudu breaks it into subtasks (flights, hotels, activities, places). You can mark items as booked as you go.'
        },
        {
          question: 'How do I mark a task important or break it down?',
          answer: 'Tap the star on a task to mark it a priority. Open a task\'s menu (⋯) for "Breakdown with AI" to split it into steps, plus edit and delete.'
        }
      ]
    },
    {
      id: 'account',
      title: 'Account & Profile',
      icon: '👤',
      content: [
        {
          question: 'How do I edit my profile?',
          answer: 'Open the Me tab to update your photo, display name, bio, and handle. You can also set your interests and store memberships there so suggestions and deals are more relevant.'
        },
        {
          question: 'Where do I set my interests and memberships?',
          answer: 'On the Me page, add the categories you care about (groceries, travel, etc.) and any store memberships you hold (Costco, Amazon Prime, …). Tudu uses these to personalize tips and deals.'
        },
        {
          question: 'How do I change app preferences?',
          answer: 'The Settings page covers appearance (dark mode, sound), regional options (week start, timezone, language), and task preferences. The Profile menu in the header links there too.'
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: '🔧',
      content: [
        {
          question: 'My tasks aren\'t saving',
          answer: 'Make sure you\'re signed in and have a stable internet connection — tasks sync to your account. If a page looks stale, refresh and check you\'re on the right day.'
        },
        {
          question: 'AI breakdown isn\'t working',
          answer: 'Travel and Discover tasks are broken down on our servers and need an internet connection. Shopping breakdowns use the in-browser assistant; if it asks for a key, that feature isn\'t configured on this build yet.'
        },
        {
          question: 'I can\'t log in',
          answer: 'Use the "Forgot Password" link on the login page to reset, or sign in with Google. If problems continue, email our support team below.'
        }
      ]
    }
  ];

  const contactOptions = [
    {
      icon: '📧',
      title: 'Email Support',
      description: 'support@tudusmartplanner.com',
      action: 'Send Email',
      href: 'mailto:support@tudusmartplanner.com'
    },
    {
      icon: '💬',
      title: 'Contact Form',
      description: 'Send us a message and we\'ll get back to you',
      action: 'Open Contact',
      href: '/contact'
    },
    {
      icon: '✨',
      title: 'Ask Tudu',
      description: 'Get instant help from the in-app assistant',
      action: 'Use the Ask button',
      href: null
    }
  ];

  const filteredSection = helpSections.find(section => section.id === activeSection);

  return (
    <div className="help-wrapper">
      <Seo
        path="/help"
        title="Help & FAQ — Tudu Smart Planner"
        description="Answers to common questions about using Tudu — tasks, routines, sharing, deals, coupons, and your account."
      />
      <div className="help-card">
        <div className="help-header">
          <h1>Help & Support</h1>
          <p>Find answers to common questions or contact our support team</p>
        </div>

        <div className="search-box">
          <input
            type="text"
            placeholder="Search for help..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="help-content">
          <div className="help-sidebar">
            {helpSections.map(section => (
              <button
                key={section.id}
                className={`sidebar-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                <span className="sidebar-icon">{section.icon}</span>
                <span>{section.title}</span>
              </button>
            ))}
          </div>

          <div className="help-main">
            <h2>{filteredSection.title}</h2>
            <div className="faq-list">
              {filteredSection.content.map((item, index) => (
                <div key={index} className="faq-item">
                  <h3 className="faq-question">{item.question}</h3>
                  <p className="faq-answer">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="contact-section">
          <h2>Still Need Help?</h2>
          <div className="contact-grid">
            {contactOptions.map((option, index) => (
              <div key={index} className="contact-card">
                <div className="contact-icon">{option.icon}</div>
                <h3>{option.title}</h3>
                <p>{option.description}</p>
                {option.href ? (
                  <a className="btn-contact" href={option.href}>{option.action}</a>
                ) : (
                  <span className="btn-contact btn-contact-static">{option.action}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HelpPage;
